<template>
  <div
    class="sv-view-switch"
    role="tablist"
    aria-label="Schedule layout"
    data-test="sv-view-switch"
  >
    <button
      v-for="tab in tabs"
      :key="tab.key"
      type="button"
      role="tab"
      class="sv-view-switch__tab"
      :class="{ 'sv-view-switch__tab--on': view === tab.key }"
      :aria-selected="view === tab.key ? 'true' : 'false'"
      :aria-label="tab.label"
      :title="tab.label"
      :data-test="tab.test"
      @click="pick(tab.key)"
    >
      <i class="fa" :class="tab.icon" aria-hidden="true"></i>
      <span class="sv-view-switch__label">{{ tab.label }}</span>
    </button>
  </div>
</template>

<script>
/**
 * The Schedule / Table view switch (Phase 11), in the controls row right after the settings gear —
 * where the design puts it (`.view-seg`).
 *
 * PRESENTATIONAL: props in, one event out. Which view is on, and remembering it, belong to the store
 * (scheduleView/updateView); whether the switch exists at all belongs to the page — a phone never
 * gets the table, so the PAGE leaves this component out (`v-if="!isPhone"`). It is deliberately NOT
 * hidden with CSS here: the JS decides, and a stylesheet that hid it on its own would let the two
 * disagree.
 *
 * Both words are hidden at tablet width and the buttons keep their icon, `title` and `aria-label`
 * (the design's tablet form), so nothing is left unnamed for a screen reader.
 */
export default {
  name: 'ScheduleViewSwitch',

  props: {
    /** 'schedule' | 'table' */
    view: { type: String, default: 'schedule' },
  },

  data() {
    return {
      tabs: [
        { key: 'schedule', label: 'Schedule', icon: 'fa-calendar', test: 'sv-view-schedule' },
        { key: 'table', label: 'Table view', icon: 'fa-table', test: 'sv-view-table' },
      ],
    };
  },

  methods: {
    /** Choosing the view that is already on is not a change — nothing is stored or reloaded for it. */
    pick(key) {
      if (key !== this.view) this.$emit('change', key);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-view-switch {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 2px;
  height: 34px;
  box-sizing: border-box;
  padding: 3px;
  border-radius: $sv-radius;
  background: $sv-bg-3;
}

.sv-view-switch__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: $sv-ink-3;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  .fa { color: $sv-ink-3; font-size: 13px; }

  &:hover:not(.sv-view-switch__tab--on) { color: $sv-ink; }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: 1px;
  }
}

.sv-view-switch__tab--on {
  background: $sv-surface;
  color: $sv-brand-deep;
  box-shadow: 0 1px 2px rgba(21, 32, 26, 0.12);

  .fa { color: $sv-brand; }
}

// The tablet form: icon only. The label is a span of its own so the words go and the icon, title and
// aria-label stay.
@media (max-width: $sv-tablet-max) {
  .sv-view-switch__tab {
    gap: 0;
    padding: 0 10px;
  }

  .sv-view-switch__label { display: none; }
}
</style>
